import type { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@wp-one/api'
import { SESSION_QUERY_KEY } from './keys'

/**
 * Why the session ended, from the API's 401 `errors[0].code` (api-wp-one,
 * docs/decisoes/2026-10-06_sessoes-renovacao-e-limite.md). The front owns the
 * wording; unknown codes show nothing special (never echo text from the URL).
 */
// User-facing messages (Portuguese).
const SIGN_OUT_MESSAGE: Record<string, string> = {
  sessao_encerrada_novo_login: 'Você entrou em outro dispositivo, por isso saímos desta sessão.',
  sessao_encerrada_sair_de_todos: 'Você saiu de todos os dispositivos.',
  sessao_encerrada_logout: 'Você saiu da sua conta.',
  sessao_encerrada_pelo_usuario: 'Esta sessão foi encerrada em outro dispositivo.',
  sessao_encerrada_redefinicao_senha: 'Sua senha foi redefinida. Entre com a nova senha.',
  sessao_encerrada_senha_alterada: 'Sua senha foi alterada em outro dispositivo. Entre com a nova senha.',
  sessao_expirada_inatividade: 'Sua sessão expirou por inatividade.',
  sessao_expirada: 'Sua sessão expirou. Entre novamente.',
  sessao_encerrada_bloqueio: 'Seu acesso foi bloqueado. Procure a secretaria.',
  sessao_encerrada: 'Sua sessão foi encerrada. Entre novamente.',
}

let lastReason: string | null = null

export function signOutMessage(code: string | null | undefined): string | null {
  return code ? (SIGN_OUT_MESSAGE[code] ?? null) : null
}

/** Remembers the reason of a 401 (only known session codes). Returns whether it was a 401. */
export function recordUnauthorized(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 401) return false
  if (error.code && error.code in SIGN_OUT_MESSAGE) lastReason = error.code
  return true
}

/** Reads and clears the last sign-out reason (used once, when building the sign-in URL). */
export function takeSignOutReason(): string | null {
  const reason = lastReason
  lastReason = null
  return reason
}

/**
 * Global 401 handler (wired in the QueryClient): any request that finds the session
 * gone marks the user as signed out, so ProtectedModule sends them to /conta/entrar
 * with the reason — no module has to handle 401 by itself.
 */
export function handleUnauthorized(client: QueryClient, error: unknown): void {
  if (recordUnauthorized(error)) client.setQueryData(SESSION_QUERY_KEY, null)
}
